import React, {useEffect, useMemo, useRef} from "react";
import {Chart, LogarithmicScale} from "chart.js/auto";
import {chartColors} from "../../mosaic/charts/ChartjsConfig.jsx";
import {useGetBackgroundColor, useGetGrayscaleBackgroundColor} from "../../utils/graphColors.js";
import {useThemeProvider} from "../../mosaic/utils/ThemeContext.jsx";
import {GraphScale, GraphType} from "./Graph.types.ts";

/**
 * @param {GraphData} data
 * @param className
 * @param style
 */
export default function Graph({data, className, style}) {
    const canvas = useRef(null);

    const {currentTheme} = useThemeProvider();

    const darkMode = useMemo(() => currentTheme === 'dark', [currentTheme]);

    const getBackgroundColor = useGetBackgroundColor();
    const getGrayscaleBackgroundColor = useGetGrayscaleBackgroundColor();

    const {
        gridColor,
        textColor,
        tooltipBodyColor,
        tooltipBgColor,
        tooltipBorderColor
    } = chartColors;

    useEffect(() => {
        const ctx = canvas.current;

        if (data.scale === GraphScale.Logarithmic) {
            Chart.register(LogarithmicScale);
        }

        const pluginsArray = [];

        const chartOptions = {
            animations: null,
            responsive: true,
            scales: {
                y: {
                    type: data.scale,
                    border: {
                        display: false,
                    },
                    ticks: {
                        display: data.showLegend !== false,
                        maxTicksLimit: 7,
                        color: darkMode ? textColor.dark : textColor.light,
                    },
                    grid: {
                        display: data.type !== GraphType.Pie,
                        color: darkMode ? gridColor.dark : gridColor.light,
                    },
                },
                x: {
                    border: {
                        display: false,
                    },
                    ticks: {
                        display: data.showLegend !== false,
                        padding: 10,
                        align: 'center',
                        color: darkMode ? textColor.dark : textColor.light,
                        callback: function (val, index) {
                            const originalLabel = Object.keys(data.datasets[0].data)[index];

                            if (data.tickRender) {
                                return data.tickRender(originalLabel);
                            }

                            return originalLabel;
                        },
                    },
                    grid: {
                        display: false,
                    },
                },
            },
            plugins: {
                tooltip: {
                    enabled: data.showTooltip !== false,
                    callbacks: {
                        title: () => false,
                        label: (context) => {
                            if (data.tooltipRender) {
                                return data.tooltipRender(context);
                            }

                            return Object.values(context.dataset.data)[context.dataIndex];
                        },
                    },
                    bodyColor: darkMode ? tooltipBodyColor.dark : tooltipBodyColor.light,
                    backgroundColor: darkMode ? tooltipBgColor.dark : tooltipBgColor.light,
                    borderColor: darkMode ? tooltipBorderColor.dark : tooltipBorderColor.light,
                    opacity: 0.8,
                },
                legend: {
                    display: false,
                },
            },
            interaction: {
                intersect: false,
                mode: 'nearest',
            },
            maintainAspectRatio: false,
            elements: {
                point: {
                    radius: 0,
                    hoverRadius: 0,
                },
            },
        };

        const newChart = new Chart(ctx, {
            type: data.type,
            data: {
                datasets: data.datasets.map((dataset, index) => {


                    let hasBackgroundColorProperty = dataset.hasOwnProperty('backgroundColor');

                    let datasetResult = {
                        label: dataset.label,
                        data: dataset.data,
                        fill: true,
                        borderWidth: 0,
                        pointBorderWidth: 0,
                    };

                    if(!hasBackgroundColorProperty)
                    {

                        datasetResult.backgroundColor = dataset.gray
                            ? getGrayscaleBackgroundColor(index, 0.2)
                            : getBackgroundColor(index, 0.7);

                        datasetResult.borderColor = dataset.gray
                            ? getGrayscaleBackgroundColor(index, 0.2)
                            : getBackgroundColor(index, 0.7);

                    }

                    return datasetResult;
                }),
            },
            options: chartOptions,
            plugins: pluginsArray,
        });

        return () => {
            newChart.destroy();

            if (data.scale === GraphScale.Logarithmic) {
                Chart.unregister(LogarithmicScale);
            }
        };
    }, [data]);

    return <canvas ref={canvas} className={className} style={style}/>;
}
